import { useState } from 'react'
import { CopySimple, Plus, Ruler, Trash } from '@phosphor-icons/react'
import { istDateKey } from '@/lib/ist'
import { Banner, DataTable, EmptyState, Field, Input, Select, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { useCreateLabLimit, useDeleteLabLimit, useLabLimits, type LabLimitRow } from '../../api/lab'
import { useCrops } from '../../api/reference'
import { ActionButton } from '../../components/Can'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { FormDialog } from '../../components/FormDialog'
import { ConsolePage } from '../../components/Layout'
import { Check, When } from '../../components/bits'
import { useUrlState } from '../../lib/useUrlState'
import {
  RULE_LABEL,
  draftFromLimit,
  emptyLabLimitDraft,
  labLimitBody,
  labLimitOk,
  trimDecimal,
  validateLabLimit,
  type LabLimitDraft,
  type LabLimitRule,
} from '../../model/lab/labLimitForm'
import { humanize } from '../../labels'

const LIMIT = 50
const PANELS = ['PESTICIDE', 'AFLATOXIN', 'HEAVY_METALS', 'MICROBIAL']

/** Lab limits (F7.5-b): per crop, test and substance, versioned — a change is a new version. */
export function LabLimitsPage() {
  const [f, setF, { reset, active }] = useUrlState({ cropId: '', panel: '', search: '', page: 1 })
  const crops = useCrops()
  const q = useLabLimits({ page: f.page, limit: LIMIT, cropId: f.cropId || undefined, panel: f.panel || undefined, search: f.search || undefined })
  const [editing, setEditing] = useState<LabLimitDraft | null>(null)
  const [removing, setRemoving] = useState<LabLimitRow | null>(null)
  const today = istDateKey()

  const columns: Column<LabLimitRow>[] = [
    { id: 'crop', header: 'Crop', cell: (r) => r.cropName ?? <span className="text-muted">All crops</span> },
    { id: 'panel', header: 'Test', cell: (r) => <span className="font-semibold">{humanize(r.panel)}</span> },
    { id: 'analyte', header: 'Substance', cell: (r) => r.analyte ?? <span className="text-muted">Whole test</span> },
    {
      id: 'rule',
      header: 'Rule',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{RULE_LABEL[r.rule as LabLimitRule] ?? humanize(r.rule)}</span>
          {r.rule === 'MAX_LIMIT' && r.limitValue && (
            <span className="font-mono text-[12.5px]">
              ≤ {trimDecimal(r.limitValue)} {r.uom}
            </span>
          )}
          {r.maxLoq && (
            <span className="font-mono text-[12px] text-muted">
              LOQ ≤ {trimDecimal(r.maxLoq)} {r.uom}
            </span>
          )}
        </span>
      ),
    },
    { id: 'release', header: 'Needed to release', cell: (r) => <Check ok={r.requiredForRelease} label={r.requiredForRelease ? 'Yes' : 'No'} />, hideBelow: 'md' },
    { id: 'from', header: 'From', cell: (r) => <When at={r.effectiveFrom} dateOnly />, hideBelow: 'sm' },
    { id: 'version', header: 'Version', align: 'right', mono: true, cell: (r) => r.version, hideBelow: 'md' },
    { id: 'source', header: 'Source', cell: (r) => <span className="text-muted">{r.source}</span>, hideBelow: 'lg' },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton perm="lab-limits:create" hide size="sm" variant="ghost" leadingIcon={<CopySimple />} onClick={() => setEditing(draftFromLimit(r, today))}>
            New version
          </ActionButton>
          <ActionButton perm="lab-limits:delete" hide size="sm" variant="ghost" leadingIcon={<Trash />} onClick={() => setRemoving(r)}>
            Retire
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Lab limits"
      description="What a lab result is judged against, per crop, test and substance. A limit is never changed: a new version applies from its date, and every result keeps the limit it was judged on."
      actions={
        <ActionButton perm="lab-limits:create" leadingIcon={<Plus />} onClick={() => setEditing({ ...emptyLabLimitDraft(today), cropId: f.cropId })}>
          New limit version
        </ActionButton>
      }
    >
      <DataTable<LabLimitRow>
        caption="Lab limits"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter label="Crop" value={f.cropId} onChange={(cropId) => setF({ cropId })} options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <SelectFilter label="Test" value={f.panel} onChange={(panel) => setF({ panel })} options={PANELS.map((p) => ({ value: p, label: humanize(p) }))} />
            <SearchFilter label="Substance" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. Chlorpyrifos" />
          </FilterBar>
        }
        empty={<EmptyState icon={<Ruler />} title={active ? 'No limits match' : 'No lab limits yet'} description="Add the limits before the first report arrives: a report with a substance that has no limit is refused." compact />}
      />
      {editing && <LabLimitDialog initial={editing} crops={crops.data ?? []} onClose={() => setEditing(null)} />}
      <RetireLimit row={removing} onClose={() => setRemoving(null)} />
    </ConsolePage>
  )
}

function LabLimitDialog({ initial, crops, onClose }: { initial: LabLimitDraft; crops: ReadonlyArray<{ id: string; name: string }>; onClose: () => void }) {
  const [d, setD] = useState<LabLimitDraft>(initial)
  const [touched, setTouched] = useState(false)
  const save = useCreateLabLimit()
  const p = validateLabLimit(d)
  const show = (k: keyof LabLimitDraft) => (touched ? p[k] : undefined)

  return (
    <FormDialog
      open
      onClose={onClose}
      title="New limit version"
      description="Saved as the next version for this crop, test and substance. Reports tested from its date on are judged by it."
      saveLabel="Add version"
      loading={save.isPending}
      onSave={async () => {
        setTouched(true)
        if (!labLimitOk(p)) throw new Error('invalid')
        await save.mutateAsync(labLimitBody(d))
      }}
    >
      {save.isError && (
        <Banner tone="error" title="Not saved">
          {errorMessage(save.error)}
        </Banner>
      )}
      <datalist id="limit-panels">
        {PANELS.map((x) => (
          <option key={x} value={x} />
        ))}
      </datalist>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Crop" hint="Empty: every crop.">
          <Select
            value={d.cropId}
            onChange={(cropId) => setD({ ...d, cropId })}
            placeholder="Every crop"
            options={[{ value: '', label: 'Every crop' }, ...crops.map((c) => ({ value: c.id, label: c.name }))]}
          />
        </Field>
        <Field label="Test" required error={show('panel')}>
          <Input list="limit-panels" value={d.panel} onChange={(e) => setD({ ...d, panel: e.target.value })} className="font-mono" maxLength={40} placeholder="PESTICIDE" />
        </Field>
        <Field label="Substance" error={show('analyte')} hint="Empty: applies to every substance of the test.">
          <Input value={d.analyte} onChange={(e) => setD({ ...d, analyte: e.target.value })} maxLength={80} placeholder="Chlorpyrifos" />
        </Field>
        <Field label="Rule" required error={show('rule')}>
          <Select
            value={d.rule}
            onChange={(rule) => setD({ ...d, rule })}
            placeholder="Choose…"
            options={(Object.keys(RULE_LABEL) as LabLimitRule[]).map((k) => ({ value: k, label: RULE_LABEL[k] }))}
          />
        </Field>
        {d.rule === 'MAX_LIMIT' && (
          <Field label="Limit" required error={show('limitValue')}>
            <Input inputMode="decimal" value={d.limitValue} onChange={(e) => setD({ ...d, limitValue: e.target.value })} className="font-mono" placeholder="0.01" trailing={d.uom || undefined} />
          </Field>
        )}
        <Field
          label="Highest LOQ accepted"
          error={show('maxLoq')}
          hint={d.rule === 'NOT_DETECTED' ? 'A test less sensitive than this cannot back "not detected".' : 'Optional.'}
        >
          <Input inputMode="decimal" value={d.maxLoq} onChange={(e) => setD({ ...d, maxLoq: e.target.value })} className="font-mono" placeholder="0.01" trailing={d.uom || undefined} />
        </Field>
        <Field label="Unit" required error={show('uom')}>
          <Input value={d.uom} onChange={(e) => setD({ ...d, uom: e.target.value })} maxLength={10} placeholder="mg/kg" />
        </Field>
        <Field label="Applies from" required error={show('effectiveFrom')}>
          <Input type="date" value={d.effectiveFrom} onChange={(e) => setD({ ...d, effectiveFrom: e.target.value })} />
        </Field>
      </div>
      <Field label="Source" required error={show('source')} hint="The regulation or standard, e.g. FSSAI Contaminants Regulations 2011, 2.1">
        <Input value={d.source} onChange={(e) => setD({ ...d, source: e.target.value })} maxLength={120} />
      </Field>
      <label className="flex items-start gap-2 text-[14px]">
        <input type="checkbox" className="mt-1" checked={d.requiredForRelease} onChange={(e) => setD({ ...d, requiredForRelease: e.target.checked })} />
        <span>
          Needed to release
          <span className="block text-[13px] text-muted">A pack lot cannot be released until this test has a checked passing result.</span>
        </span>
      </label>
    </FormDialog>
  )
}

function RetireLimit({ row, onClose }: { row: LabLimitRow | null; onClose: () => void }) {
  const del = useDeleteLabLimit()
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={onClose}
      title={`Retire version ${row?.version ?? ''} of ${row ? humanize(row.panel) : ''}${row?.analyte ? ` · ${row.analyte}` : ''}?`}
      description="New reports are no longer judged by it; results already judged keep this limit. If no other version applies, reports with this substance are refused until a new one is added."
      confirmLabel="Retire"
      tone="danger"
      loading={del.isPending}
      onConfirm={async () => {
        if (!row) return
        await del.mutateAsync(row.id)
        onClose()
      }}
    />
  )
}
